import { getApiPrefix } from '../utils/apiHelper';
import { useState, useEffect, useCallback } from "react";
import { api } from "../App";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { handleApiError, showSuccess } from "../utils/errorHandler";
import { PageLoader } from "../components/ui/page-loader";
import { Loader2, RefreshCcw, ChevronLeft, ChevronRight, TrendingUp, TrendingDown, Wallet, FileText, Landmark, CreditCard } from "lucide-react";
import { useTranslation } from "../contexts/I18nContext";

const PAGE_SIZE = 50;

const MESI = [
  { value: "all", label: "Tutti i mesi" },
  { value: "1", label: "Gennaio" },
  { value: "2", label: "Febbraio" },
  { value: "3", label: "Marzo" },
  { value: "4", label: "Aprile" },
  { value: "5", label: "Maggio" },
  { value: "6", label: "Giugno" },
  { value: "7", label: "Luglio" },
  { value: "8", label: "Agosto" },
  { value: "9", label: "Settembre" },
  { value: "10", label: "Ottobre" },
  { value: "11", label: "Novembre" },
  { value: "12", label: "Dicembre" }
];

export default function MovimentiPatrimonialiPage() {
  const { t } = useTranslation();
  const MESI = [
    { value: '01', label: t('months.january') },
    { value: '02', label: t('months.february') },
    { value: '03', label: t('months.march') },
    { value: '04', label: t('months.april') },
    { value: '05', label: t('months.may') },
    { value: '06', label: t('months.june') },
    { value: '07', label: t('months.july') },
    { value: '08', label: t('months.august') },
    { value: '09', label: t('months.september') },
    { value: '10', label: t('months.october') },
    { value: '11', label: t('months.november') },
    { value: '12', label: t('months.december') },
  ];
  const currentYear = new Date().getFullYear();
  const [loading, setLoading] = useState(true);
  const [anno, setAnno] = useState(String(currentYear));
  const [mese, setMese] = useState("all");
  const [data, setData] = useState(null);
  const [page, setPage] = useState(1);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ anno, page: String(page), page_size: String(PAGE_SIZE) });
      if (mese && mese !== "all") params.append("mese", mese);
      
      const res = await api.get(`${getApiPrefix()}/reports/movimenti-patrimoniali?${params.toString()}`);
      setData(res.data);
    } catch (err) {
      handleApiError(err, t("errors.loadFailed"));
      
    } finally {
      setLoading(false);
    }
  }, [anno, mese, page]);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { setPage(1); }, [anno, mese]);

  const formatCurrency = (val) => new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(val || 0);

  const getFonteIcon = (fonte) => {
    switch (fonte) {
      case 'fattura': return <FileText className="h-4 w-4 text-blue-600" />;
      case 'movimento_banca': return <Landmark className="h-4 w-4 text-emerald-600" />;
      case 'movimento_carta': return <CreditCard className="h-4 w-4 text-violet-600" />;
      default: return null;
    }
  };

  const getFonteBadge = (fonte) => {
    switch (fonte) {
      case 'fattura': return <Badge className="bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">{t('invoices.title')}</Badge>;
      case 'movimento_banca': return <Badge className="bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400">Mov. Banca</Badge>;
      case 'movimento_carta': return <Badge className="bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-400">Mov. Carta</Badge>;
      default: return <Badge variant="outline">{t('common.other')}</Badge>;
    }
  };

  return (
    <div className="space-y-6" data-testid="movimenti-patrimoniali-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">{t("nav.equityMovements")}</h1>
          <p className="text-muted-foreground">Monitoraggio conti 1xx (Attivo) e 2xx (Passivo)</p>
        </div>
        <Button variant="outline" onClick={fetchData}>
          <RefreshCcw className="h-4 w-4 mr-2" />{t('common.refresh')}</Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-4">
          <div className="flex flex-wrap gap-4 items-center">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium">Anno:</span>
              <Select value={anno} onValueChange={setAnno}>
                <SelectTrigger className="w-24">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[currentYear, currentYear - 1, currentYear - 2].map(y => (
                    <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium">Mese:</span>
              <Select value={mese} onValueChange={setMese}>
                <SelectTrigger className="w-40">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {MESI.map(m => (
                    <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : data && (
        <>
          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <TrendingUp className="h-4 w-4 text-emerald-600" />
                  Attivo (Conti 1xx)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-2xl font-bold text-emerald-600">{formatCurrency(data.totale_attivo)}</p>
                <p className="text-xs text-muted-foreground">Crediti, anticipi, depositi</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <TrendingDown className="h-4 w-4 text-red-600" />
                  Passivo (Conti 2xx)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-2xl font-bold text-red-600">{formatCurrency(data.totale_passivo)}</p>
                <p className="text-xs text-muted-foreground">Debiti, acconti, ratei</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <Wallet className="h-4 w-4 text-blue-600" />{t('common.netBalance')}</CardTitle>
              </CardHeader>
              <CardContent>
                <p className={`text-2xl font-bold ${data.saldo >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                  {formatCurrency(data.saldo)}
                </p>
                <p className="text-xs text-muted-foreground">Attivo - Passivo</p>
              </CardContent>
            </Card>
          </div>

          {/* Per Conto Summary */}
          {data.per_conto && data.per_conto.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Riepilogo per Conto</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {data.per_conto.map((conto, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                      <div className="flex items-center gap-2">
                        <Badge variant={conto.tipo === 'attivo' ? 'default' : 'destructive'} className="text-xs">
                          {conto.conto_codice}
                        </Badge>
                        <span className="text-sm truncate block" style={{ maxWidth: '8rem' }} title={conto.conto_nome}>{conto.conto_nome}</span>
                      </div>
                      <div className="text-right">
                        <p className={`font-medium ${conto.tipo === 'attivo' ? 'text-emerald-600' : 'text-red-600'}`}>
                          {formatCurrency(conto.totale)}
                        </p>
                        <p className="text-xs text-muted-foreground">{conto.count} mov.</p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Table */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">{t('common.movementDetails')}</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="movimenti-patrimoniali-table">
                  <thead>
                    <tr className="border-b bg-muted/50">
                      <th className="text-left p-3 text-sm font-medium">{t("common.date")}</th>
                      <th className="text-left p-3 text-sm font-medium">Fonte</th>
                      <th className="text-left p-3 text-sm font-medium">{t("common.description")}</th>
                      <th className="text-left p-3 text-sm font-medium">{t('movements.account')}</th>
                      <th className="text-right p-3 text-sm font-medium">{t("common.amount")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.items && data.items.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="text-center p-8 text-muted-foreground">
                          Nessun movimento patrimoniale nel periodo selezionato
                        </td>
                      </tr>
                    ) : (
                      data.items && data.items.map((item, idx) => (
                        <tr key={idx} className="border-b hover:bg-muted/30 transition-colors">
                          <td className="p-3 text-sm whitespace-nowrap">{item.data}</td>
                          <td className="p-3">
                            <div className="flex items-center gap-2">
                              {getFonteIcon(item.fonte)}
                              {getFonteBadge(item.fonte)}
                            </div>
                          </td>
                          <td className="p-3 text-sm" style={{ maxWidth: '300px' }}>
                            <span className="block truncate hover:whitespace-normal hover:overflow-visible cursor-help" title={item.descrizione}>
                              {item.descrizione}
                            </span>
                          </td>
                          <td className="p-3">
                            <div className="flex items-center gap-2">
                              <Badge variant={item.tipo === 'attivo' ? 'default' : 'destructive'} className="text-xs">
                                {item.conto_codice}
                              </Badge>
                              <span className="text-sm truncate block" style={{ maxWidth: '6rem' }} title={item.conto_nome}>{item.conto_nome}</span>
                            </div>
                          </td>
                          <td className={`p-3 text-right font-medium whitespace-nowrap ${item.tipo === 'attivo' ? 'text-emerald-600' : 'text-red-600'}`}>
                            {formatCurrency(item.importo)}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          {/* Pagination */}
          <div className="flex items-center justify-between">
            <div className="text-sm text-muted-foreground">
              Pagina {data.page} di {data.total_pages} ({data.total} movimenti)
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>
                <ChevronLeft className="h-4 w-4 mr-1" />{t('common.previous')}</Button>
              <Button variant="outline" size="sm" disabled={page >= data.total_pages} onClick={() => setPage(p => p + 1)}>{t('common.nextLabel')}<ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
